<template>
  <svg
    ref="handle"
    v-b-tooltip="tooltip"
    xmlns="http://www.w3.org/2000/svg"
    height="200"
    width="200"
    viewBox="0 0 350 350"
    class="bubbles-handle"
    :style="style"
    @mouseenter="onHover"
  >
    <path
      d="M 55.437167,322.79459 H 24.041631 C 10.748022,322.79459 -1e-6,312.04657 0.07071,298.82367 L 0,23.971268 C 6e-6,10.677656 10.748024,-0.07035815 23.970929,3.4685499e-4 l 274.781681,3e-6 c 13.29362,-7e-6 24.04164,10.74801614501 23.97092,23.97091814501 v 31.39554 c 0,13.43503 -11.10157,24.25377 -24.53659,23.97092 L 131.94613,75.307218 335.16862,278.52971 c 9.40452,9.40452 9.40452,24.5366 0,33.94112 l -22.62743,22.62743 c -9.40452,9.40452 -24.53659,9.40451 -33.94111,-10e-6 L 75.377591,131.87576 l 4.0305,166.38223 c 0.35356,13.50574 -10.46518,24.60731 -23.97092,24.5366 z"
      :fill="fill"
      stroke="white"
      stroke-width="15"
    />
  </svg>
</template>

<script lang="ts">

import Vue from 'vue'
import Component from 'vue-class-component'

import _cloneDeep from 'lodash/cloneDeep'
import interact from 'interactjs'
import {
  getLevelBasedPosition,
  transformCoordinatesScreenToBubbles,
  transformCoordinatesBubblesToScreen,
} from '@/components/public/Bubbles/bubblesVisualisationHelpers'
import { NODE_CONFIG_KEY, defaultLevelBasedPositions } from '@/constants'
import { Coordinate2D, NodeConfigKeys, TreeNode } from '@/types'

const INITIAL_SIZE = 200

const props = {
  details: {
    type: TreeNode,
    required: true,
  },
}

// eslint-disable-next-line no-use-before-define
@Component<BubblesHandle>({
  watch: {
    position () {
      this.top = this.position.y
      this.left = this.position.x
    },
  },
})
export default class BubblesHandle extends Vue.extend({ props }) {
    top: number = 0
    left: number = 0

    mounted () {
      this.top = this.position.y
      this.left = this.position.x

      const bubblesSquare = document.querySelector('#bubblesMinSizeSquare')
      const modifiers = [
        interact.modifiers.restrict({
          restriction: bubblesSquare as HTMLElement,
          elementRect: {
            top: 0,
            left: 0,
            bottom: 0.2,
            right: 0.2,
          },
        }),
      ]
      interact(this.$refs.handle as HTMLElement).draggable({
        modifiers,
        onmove: this.onmove,
        onend: this.onend,
      })
    }

    get tooltip () {
      return {
        customClass: 'handle-tooltip',
        title: this.details.path.toString(),
        trigger: 'hover',
      }
    }

    get bubblesVisualisationScaleFactor () {
      return this.$typedStore.public.size.bubblesVisualisationScaleFactor
    }

    get bubblesMinSizeSquareOffset () {
      return this.$typedStore.public.size.bubblesMinSizeSquareOffset
    }

    get position (): Coordinate2D {
      const bubblePositions = this.$typedStore.visualisationData.bubblePositions
      const viewerLevel = this.$typedStore.visualisationData.viewerLevel
      const position = getLevelBasedPosition(this.details.path.toString(), bubblePositions, viewerLevel)

      return transformCoordinatesBubblesToScreen(position, this.bubblesVisualisationScaleFactor, this.bubblesMinSizeSquareOffset)
    }

    set position (newValue: Coordinate2D) {
      const position = transformCoordinatesScreenToBubbles(newValue)
      const viewerLevel = this.$typedStore.visualisationData.viewerLevel
      const bubblePositions = _cloneDeep(this.$typedStore.primary.entities.nodes.bubblePositions(this.details.path)) ?? defaultLevelBasedPositions()

      // If the bubbles are being configured at the subroot level
      // then set the position as a level override
      if (viewerLevel === 0) {
        if (!bubblePositions.levelOverrides) {
          bubblePositions.levelOverrides = {}
        }
        bubblePositions.levelOverrides[viewerLevel] = position
      } else {
        bubblePositions.default = position
      }

      this.$typedStore.primary.entities.nodes.updateNodeProp({
        path: this.details.path.toString(),
        configPath: [NODE_CONFIG_KEY, NodeConfigKeys.BUBBLE_POSITIONS],
        value: bubblePositions,
      })
    }

    get level1 () {
      return this.details.path.level1
    }

    get fill () {
      return this.$typedStore.activeVisualisation.visualisationStore.entities.nodes.colour(this.level1) ?? 'black'
    }

    get size () {
      return INITIAL_SIZE * this.bubblesVisualisationScaleFactor
    }

    get style () {
      return {
        top: `${this.top}px`,
        left: `${this.left}px`,
        width: `${this.size}px`,
        height: `${this.size}px`,
      }
    }

    onHover () {
      document.documentElement.style.setProperty('--bubbles-handle-color', this.fill)
    }

    onmove (event: any) {
      // Incrementally change the position of the header
      this.top += event.dy
      this.left += event.dx
    }

    onend () {
      this.position = {
        x: this.left,
        y: this.top,
      }
    }
}

</script>

<style scoped>
  .bubbles-handle {
    position: absolute;
    touch-action: none;
    user-select: none;
  }
</style>

<style>
  .handle-tooltip .tooltip-inner {
    font-size: 1.1em;
    font-weight: bold;
    white-space: nowrap;
    max-width: none;
    background-color: white;
    color: var(--bubbles-handle-color, black);
    border: 2px solid;
    border-color: var(--bubbles-handle-color, black);
  }

  .handle-tooltip .arrow::before {
    border-top-color: var(--bubbles-handle-color, black);
  }
</style>
